import { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Shield,
  FileCheck,
  AlertTriangle,
  Package,
  BarChart3,
  Settings,
  Bell,
  Activity,
  Database,
  Bug,
  Rocket,
  DollarSign,
  Menu,
  X,
  Mail,
  ShieldCheck,
  Zap,
  Moon,
  Sun,
  Volume2,
  VolumeX,
  Check,
  Clock,
  ChevronRight,
} from 'lucide-react';
import './Layout.css';

interface Notification {
  id: string;
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  time: string;
  read: boolean;
  link?: string;
}

// Nav items organized by section
const navSections = [
  {
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Overview' },
      { to: '/events', icon: Zap, label: 'Activity' },
    ],
  },
  {
    label: 'Service Desk',
    items: [
      { to: '/support', icon: Mail, label: 'Support Tickets' },
      { to: '/approvals', icon: ShieldCheck, label: 'Approvals' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/security', icon: Bug, label: 'Security' },
      { to: '/operations', icon: Rocket, label: 'Deployments' },
      { to: '/incidents', icon: AlertTriangle, label: 'Incidents' },
    ],
  },
  {
    label: 'Compliance',
    items: [
      { to: '/controls', icon: Shield, label: 'Controls' },
      { to: '/evidence', icon: FileCheck, label: 'Evidence' },
    ],
  },
  {
    label: 'Portfolio',
    items: [
      { to: '/products', icon: Package, label: 'Products' },
      { to: '/cmdb', icon: Database, label: 'CMDB' },
      { to: '/finops', icon: DollarSign, label: 'FinOps' },
    ],
  },
  {
    label: 'Analytics',
    items: [
      { to: '/insights', icon: Activity, label: 'Insights' },
      { to: '/reports', icon: BarChart3, label: 'Reports' },
    ],
  },
];

export default function Layout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: '1',
      type: 'warning',
      title: 'Budget Alert',
      message: 'AI costs at 75% of monthly budget',
      time: '2 hours ago',
      read: false,
      link: '/finops',
    },
    {
      id: '2',
      type: 'error',
      title: 'Critical Vulnerability',
      message: 'New critical CVE detected in DaVoid',
      time: '5 hours ago',
      read: false,
      link: '/security',
    },
    {
      id: '3',
      type: 'info',
      title: 'Deployment Complete',
      message: 'FeedRunner deployed to production',
      time: '1 day ago',
      read: true,
      link: '/operations',
    },
  ]);
  const [darkMode, setDarkMode] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const notificationRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menu when route changes
  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const handleNotificationClick = (notif: Notification) => {
    // Mark as read
    setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, read: true } : n));
    // Navigate if link exists
    if (notif.link) {
      setShowNotifications(false);
      navigate(notif.link);
    }
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'warning': return <AlertTriangle size={16} className="notif-icon warning" />;
      case 'error': return <Bug size={16} className="notif-icon error" />;
      case 'success': return <Check size={16} className="notif-icon success" />;
      default: return <Bell size={16} className="notif-icon info" />;
    }
  };

  return (
    <div className="layout">
      {/* Mobile overlay */}
      <div
        className={`sidebar-overlay ${mobileMenuOpen ? 'visible' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />

      <aside className={`sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="logo">
            <Shield size={28} />
            <span>Resolvr</span>
          </div>
          <span className="logo-subtitle">Operations Hub</span>
        </div>

        <nav className="sidebar-nav">
          {navSections.map((section, idx) => (
            <div key={idx} className="nav-section">
              {section.label && (
                <div className="nav-section-label">{section.label}</div>
              )}
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `nav-item ${isActive ? 'active' : ''}`
                  }
                  onClick={handleNavClick}
                >
                  <item.icon size={20} />
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <button className="nav-item" onClick={() => setShowSettings(true)}>
            <Settings size={20} />
            <span>Settings</span>
          </button>
        </div>
      </aside>

      <main className="main-content">
        <header className="top-bar">
          <button
            className="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <div className="search-container">
            <input
              type="text"
              placeholder="Search..."
              className="search-input"
            />
          </div>
          <div className="top-bar-actions">
            <div className="notification-wrapper" ref={notificationRef}>
              <button
                className="notification-btn"
                onClick={() => setShowNotifications(!showNotifications)}
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="notification-badge">{unreadCount}</span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="notification-dropdown">
                  <div className="notification-dropdown-header">
                    <h3>Notifications</h3>
                    {unreadCount > 0 && (
                      <button className="mark-all-read" onClick={markAllRead}>
                        Mark all read
                      </button>
                    )}
                  </div>
                  <div className="notification-list">
                    {notifications.length === 0 ? (
                      <div className="notification-empty">
                        <Bell size={24} />
                        <p>No notifications</p>
                      </div>
                    ) : (
                      notifications.map(notif => (
                        <div
                          key={notif.id}
                          className={`notification-item ${notif.read ? 'read' : 'unread'} ${notif.link ? 'clickable' : ''}`}
                          onClick={() => handleNotificationClick(notif)}
                        >
                          {getNotificationIcon(notif.type)}
                          <div className="notification-content">
                            <div className="notification-title">{notif.title}</div>
                            <div className="notification-message">{notif.message}</div>
                            <div className="notification-time">
                              <Clock size={12} />
                              {notif.time}
                            </div>
                          </div>
                          {notif.link && <ChevronRight size={16} className="notification-arrow" />}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="user-avatar">MP</div>
          </div>
        </header>
        <div className="content-area">
          <Outlet />
        </div>
      </main>

      {/* Settings Modal */}
      {showSettings && (
        <div className="modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="modal settings-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Settings</h2>
              <button className="close-btn" onClick={() => setShowSettings(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="settings-section">
                <h3>Appearance</h3>
                <div className="setting-item">
                  <div className="setting-info">
                    <div className="setting-label">
                      {darkMode ? <Moon size={18} /> : <Sun size={18} />}
                      Dark Mode
                    </div>
                    <div className="setting-description">Use dark theme for the interface</div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={darkMode}
                      onChange={(e) => setDarkMode(e.target.checked)}
                    />
                    <span className="toggle-slider" />
                  </label>
                </div>
              </div>

              <div className="settings-section">
                <h3>Notifications</h3>
                <div className="setting-item">
                  <div className="setting-info">
                    <div className="setting-label">
                      {soundEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
                      Sound Alerts
                    </div>
                    <div className="setting-description">Play sounds for new notifications</div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={soundEnabled}
                      onChange={(e) => setSoundEnabled(e.target.checked)}
                    />
                    <span className="toggle-slider" />
                  </label>
                </div>
              </div>

              <div className="settings-section">
                <h3>Integrations</h3>
                <div className="integration-list">
                  <div className="integration-item">
                    <div className="integration-icon discord">D</div>
                    <div className="integration-info">
                      <div className="integration-name">Discord</div>
                      <div className="integration-status connected">Connected</div>
                    </div>
                  </div>
                  <div className="integration-item">
                    <div className="integration-icon github">G</div>
                    <div className="integration-info">
                      <div className="integration-name">GitHub</div>
                      <div className="integration-status connected">Connected</div>
                    </div>
                  </div>
                  <div className="integration-item">
                    <div className="integration-icon vanta">V</div>
                    <div className="integration-info">
                      <div className="integration-name">Vanta</div>
                      <div className="integration-status disconnected">Not configured</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="settings-section">
                <h3>About</h3>
                <div className="about-info">
                  <p><strong>Resolvr</strong> v1.0.0</p>
                  <p>Agentic IT Department System</p>
                  <p className="text-muted">PickBits.AI Product Portfolio</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
